import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { SectionTitle } from 'components';
import { Link } from 'gatsby';
import { scrollToAnchor } from 'utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from '@reach/router';
import PrevArrowSVG from 'images/shared/PrevArrow';
import { AxEngage, KolExchange, CampaignX } from 'images/products';
import { wrap } from 'utils/wrap';

const platforms = [
  {
    title: 'KOLexchange',
    icon: KolExchange,
    summary:
      'Capture KOL insights in real time using our secure, compliant web based platform. Transform these insights into actions with live dashboards, governed workflows, and instant reporting.',
    link: '/proprietary-platforms/#KOLexchange'
  },
  {
    title: 'AX Engage',
    icon: AxEngage,
    summary:
      'AX Engage is our secure, customisable virtual events platform that inspires, educates and engages healthcare audiences. It combines broadcast expertise, digital marketing, interactive livestream production and real-time event insights and post event analytics.',
    link: '/proprietary-platforms/#AX Engage'
  },
  {
    title: 'Campaign X',
    icon: CampaignX,
    summary:
      'Campaign X is our gamified collaboration platform that empowers remote teams to create, launch and own best-in-class, commercially ready, strategic marketing campaigns.',
    link: '/proprietary-platforms/#Campaign X'
  }
];

const serviceVariants = {
  hover: {
    cursor: 'pointer',
    scale: 1.02,
    transition: {
      type: 'tween',
      ease: 'easeInOut',
      duration: 0.2
    }
  }
};

const variants = {
  enter: (direction) => {
    return {
      x: direction > 0 ? 100 : -100,
      opacity: 0
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 100 : -100,
      opacity: 0
    };
  }
};

const ProprietaryPlatforms = () => {
  const location = useLocation();
  const { pathname } = location;
  const [[page, direction], setPage] = useState([0, 0]);
  const paginate = (dir) => {
    setPage([page + dir, dir]);
  };
  const index = wrap(0, platforms.length, page);

  const handlePlatformClick = (e, platform) => {
    e.preventDefault();
    if (pathname === '/proprietary-platforms/') {
      // If we're already on the proprietary platforms page, just scroll to the anchor
      scrollToAnchor(platform.title, { offset: -100, duration: 800 });
    } else {
      // Navigate to the proprietary platforms page and then scroll
      window.location.href = platform.link;
    }
  };

  useEffect(() => {
    if (location.state?.scrollToProprietaryPlatforms) {
      scrollToAnchor('proprietary-platforms');
    }
  }, [location.pathname]);

  return (
    <OuterContainer id="proprietary-platforms">
      <div>
        {pathname === '/' && (
          <SectionTitle style={{ margin: '-0.1rem 0 0.05rem' }}>Proprietary Platforms</SectionTitle>
        )}
        <Mobile>
          <MobilePlatforms>
            <Container
              key={page}
              id={platforms[index]}
              alt="slide"
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                duration: 0.5
              }}
              onPan={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);
                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}>
              <AnimatePresence initial={false}>
                <motion.div key={platforms[index].title}>
                  <Link
                    to={platforms[index].link}
                    state={{ previousPageWasHomepage: true }}
                    title={platforms[index].title.replaceAll('<br aria-hidden="true" />', ' ')}
                    style={{ textDecoration: 'none' }}
                    onClick={(e) => handlePlatformClick(e, platforms[index])}
                    id={`see-more-${platforms[index].title
                      .toLowerCase()
                      .replaceAll(' ', '-')
                      .replaceAll('&', 'and')}`}>
                    <Platform
                      variants={serviceVariants}
                      id={`see-more-${platforms[index].title
                        .toLowerCase()
                        .replaceAll(' ', '-')
                        .replaceAll('&', 'and')}`}>
                      {(() => {
                        const IconComponent = platforms[index].icon;
                        return (
                          <Icon>
                            <IconComponent />
                          </Icon>
                        );
                      })()}
                      <p>{platforms[index].summary}</p>
                      <SeeMore
                        variants={serviceVariants}
                        id={`see-more-${platforms[index].title
                          .toLowerCase()
                          .replaceAll(' ', '-')
                          .replaceAll('&', 'and')}`}>
                        Learn More
                      </SeeMore>
                    </Platform>
                  </Link>
                </motion.div>
              </AnimatePresence>
            </Container>
            <ButtonWrapper>
              <ButtonBack type="button" aria-label="Previous Service" onClick={() => paginate(-1)}>
                <PrevArrow />
              </ButtonBack>
              <ButtonNext type="button" aria-label="Next Service" onClick={() => paginate(1)}>
                <NextArrow />
              </ButtonNext>
            </ButtonWrapper>
          </MobilePlatforms>
        </Mobile>
        <Desktop>
          <Platforms>
            {platforms.map((platform) => (
              <motion.li
                key={platform.title}
                variants={serviceVariants}
                whileHover="hover"
                whileTap="tap"
                layout>
                <Link
                  to={platform.link}
                  state={{ previousPageWasHomepage: true }}
                  title={platform.title.replaceAll('<br aria-hidden="true" />', ' ')}
                  style={{ textDecoration: 'none', margin: '0.5rem' }}
                  onClick={(e) => handlePlatformClick(e, platform)}
                  id={`see-more-${platform.title
                    .toLowerCase()
                    .replaceAll(' ', '-')
                    .replaceAll('&', 'and')}`}>
                  <Platform
                    variants={serviceVariants}
                    id={`see-more-${platform.title
                      .toLowerCase()
                      .replaceAll(' ', '-')
                      .replaceAll('&', 'and')}`}>
                    {(() => {
                      const IconComponent = platform.icon;
                      return (
                        <Icon>
                          <IconComponent />
                        </Icon>
                      );
                    })()}
                    <p>{platform.summary}</p>
                    <SeeMore
                      variants={serviceVariants}
                      id={`see-more-${platform.title
                        .toLowerCase()
                        .replaceAll(' ', '-')
                        .replaceAll('&', 'and')}`}>
                      Learn More
                    </SeeMore>
                  </Platform>
                </Link>
              </motion.li>
            ))}
          </Platforms>
        </Desktop>
      </div>
    </OuterContainer>
  );
};

const swipeConfidenceThreshold = 100;
const swipePower = (offset, velocity) => {
  return Math.abs(offset) * velocity;
};

const OuterContainer = styled.section`
  background-color: var(--ax-beige);
  > div {
    padding-top: 2.05rem;
    grid-column: 2 / 3;
    max-width: 1577px;
    margin: 0 auto;
    width: 100%;

    /* The carousel adds unwanted padding around the slides. Hiding the overflow solves this. */
    overflow: hidden;

    @media screen and (min-width: 64rem) {
      grid-column: 2 / 3;
      padding: 6rem 0;
      overflow: visible;
    }
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  position: absolute;
  left: initial;
  bottom: initial;
  right: 0.625rem !important;
  width: 7.188rem;
  top: -1rem !important;
  z-index: 2;
  height: 3rem;
`;

const Icon = styled(motion.div)`
  width: auto;
  height: 30px;
  margin-bottom: 2rem;
  svg {
    width: auto !important;
    height: 26px !important;
    margin-bottom: 0 !important;
  }
`;

const ButtonStyle = css`
  align-items: center;
  background-color: var(--ax-gold);
  border-radius: 0.125rem;
  display: flex;
  height: 3rem;
  justify-content: center;
  outline: none;
  transition: background-color 150ms ease-in-out;
  width: 3rem;

  &[disabled] {
    background-color: var(--ax-grey);
  }

  &:active {
    background-color: #000;
    transition: background-color 75ms ease-in-out;
  }
`;

const ButtonBack = styled.button`
  ${ButtonStyle}
`;

const ButtonNext = styled.button`
  ${ButtonStyle}
`;

const PrevArrow = styled(PrevArrowSVG)`
  height: 0.938rem;
  width: 1.688rem;
`;

const NextArrow = styled(PrevArrow)`
  transform: scaleX(-1);
`;

const SeeMore = styled(motion.div)`
  align-items: center;
  border-radius: 0.125em;
  border: 0.063em solid var(--ax-gold);
  color: var(--ax-gold);
  display: flex;
  font-size: 1rem;
  font-weight: 700;
  height: 3em;
  justify-content: center;
  text-transform: uppercase;
  width: 10.375em;

  @media screen and (min-width: 64rem) {
    border: none;
    bottom: 0;
    color: #000;
    display: block;
    font-weight: 700;
    height: unset;
    margin-bottom: 0.3em;
    pointer-events: none;
    position: absolute;
    width: unset;
  }
`;

const MobilePlatforms = styled.div`
  margin: 0 auto;
  position: relative;
  max-width: 100%;
  list-style-type: none;
  padding: 0;

  a {
    text-decoration: none;
  }

  @media screen and (max-width: 39.75rem) {
    max-width: 28.125rem;
  }

  @media screen and (min-width: 64rem) {
    display: grid;
    grid-gap: 95px 86px;
    grid-template-columns: repeat(2, 250px);
    grid-template-rows: 300px 300px;
    justify-content: center;
    padding-top: 3.7rem;
  }

  @media screen and (min-width: 80rem) {
    grid-template-rows: 300px;
    grid-template-columns: repeat(4, minmax(auto, 250px));
    justify-content: space-between;
    grid-gap: 0;
    /* max-width: 73.75rem; */
    width: 100%;
  }
`;

const Platforms = styled.ul`
  margin: 0 auto;
  position: relative;
  max-width: 100%;
  list-style-type: none;
  padding: 0;

  a {
    text-decoration: none;
  }

  li:first-child {
    ${Icon} svg {
      height: 30px !important;
    }
  }

  @media screen and (max-width: 39.75rem) {
    max-width: 28.125rem;
  }

  @media screen and (min-width: 64rem) {
    display: grid;
    grid-gap: 95px 86px;
    grid-template-columns: repeat(3, 250px);
    justify-content: center;
    padding-top: 3.7rem;
  }

  @media screen and (min-width: 1280px) {
    grid-template-rows: 300px;
    grid-template-columns: repeat(3, minmax(auto, 350px));
    justify-content: space-between;
    grid-gap: 0;
    /* max-width: 73.75rem; */
    width: 100%;
  }

  @media screen and (min-width: 1600px) {
    grid-template-columns: repeat(3, minmax(auto, 400px));
  }
`;

const Platform = styled(motion.div)`
  background-color: #fff;
  height: 100%;
  margin-top: 1.1rem;
  max-height: 26.375rem;
  padding: 5rem 1.3rem 1.438rem 1.438rem;
  width: calc(100% - 1.25rem);

  ul {
    li {
      font-size: 1rem;
      line-height: 2em;
    }
  }

  @media screen and (min-width: 351px) {
    max-height: 30rem;
  }

  svg {
    margin-bottom: 1.28rem;
    width: 2.5rem;
    height: 2.313rem;

    g {
      transition: stroke 200ms ease-in-out;
      stroke-width: 1.25px;
    }
  }
  h3 {
    color: var(--ax-gold);
    margin-bottom: 0.6em;
    font-size: 2rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-wrap: wrap;
    transition: 0.3s;
    svg {
      width: auto;
      height: 0.9rem;
      margin-top: 1rem;
      margin-bottom: 0;
    }
  }

  p {
    color: #000;
    font-size: 0.875rem;
    line-height: 1.429em;
    margin-bottom: 3.3em;
    max-width: 92.5%;
  }

  ul {
    list-style-type: none;
    font-size: 1rem;
    line-height: 1.375em;
    color: #000;
    margin-bottom: 1.75em;
  }
  ${SeeMore} {
    transition: 0.3s;
  }
  &:hover {
    /* svg g {
      stroke: var(--ax-gold);
    } */
    h3 {
      color: var(--ax-gold);
      font-weight: 900;
    }
    ${SeeMore} {
      color: var(--ax-gold);
    }
  }

  &:not(:hover) {
    svg g {
      stroke: #000;
    }

    ul {
      font-weight: 400;
    }
  }

  @media screen and (min-width: 64rem) {
    background-color: transparent;
    margin-top: 0;
    padding: 0;
    width: 100%;
    position: relative;
    border-bottom-style: solid;
    border-bottom-width: 2px;
    border-bottom-color: #000;
    transition: 0.3s;
    &:hover {
      border-bottom: 2px solid var(--ax-gold);
    }

    svg {
      margin-bottom: 1.2rem;
    }

    h3 {
      font-weight: 100;
      margin-bottom: 1rem;
      color: #000;
      transition: font-weight 0.25s ease-in-out;
    }

    p {
      max-width: 100%;
    }

    ul {
      margin-bottom: 0;
    }
  }
`;

const Container = styled(motion.div)`
  width: 100%;
  height: auto;
  position: relative;
`;

const Mobile = styled.div`
  display: block;
  @media (min-width: 64rem) {
    display: none;
  }
`;

const Desktop = styled.div`
  display: none;
  @media (min-width: 64rem) {
    display: block;
  }
`;

export default ProprietaryPlatforms;
